import { useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import trim from 'lodash-es/trim';

import { B3CustomForm } from '@/components';
import CustomButton from '@/components/button/CustomButton';
import B3Spin from '@/components/spin/B3Spin';
import useStorageState from '@/hooks/useStorageState';
import PaymentMethod from '@/pages/AccountSetting/components/PaymentMethod';
import { CustomStyleContext } from '@/shared/customStyleButton';
import {
  checkUserBCEmail,
  checkUserEmail,
  getB2BAccountFormFields,
  getB2BAccountSettings,
  getBCAccountSettings,
  getCompanyData,
  updateB2BAccountSettings,
  updateBCAccountSettings,
} from '@/shared/service/b2b';
import { fetchCompanyOpenAmounts, fetchCustomerAccount } from '@/shared/service/request/alumio';
import { isB2BUserSelector, useAppSelector } from '@/store';
import { Fields, ParamProps } from '@/types/accountSetting';
import { B3SStorage, channelId, formatCurrency, snackbar } from '@/utils';

import { deCodeField, getAccountFormFields } from '../Registered/config';

import { getAccountSettingsFields, getPasswordModifiedFields } from './config';
import { b2bSubmitDataProcessing, bcSubmitDataProcessing, initB2BInfo, initBcInfo } from './utils';

type PaymentFormItem = {
  PaymentFormCode: string;
};

type CreditLimitAmount = {
  '@currencyCode': string;
  '#': string;
};

export interface PaymentDataItem {
  CompanyID: string;
  UUID: string;
  AccountDeterminationDebtorGroupCode: string;
  PaymentForm: PaymentFormItem | PaymentFormItem[];
  CreditLimitAmount: CreditLimitAmount;
  DirectDebitBankDetailsID?: string;
}

function useData() {
  const isB2BUser = useAppSelector(isB2BUserSelector);
  const companyInfoId = useAppSelector(({ company }) => company.companyInfo.id);
  const customer = useAppSelector(({ company }) => company.customer);
  const role = useAppSelector(({ company }) => company.customer.role);
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);
  const companyId = role === 3 && isAgenting ? Number(salesRepCompanyId) : Number(companyInfoId);
  const isBCUser = !isB2BUser || (role === 3 && !isAgenting);

  const validateEmailValue = async (emailValue: string) => {
    if (customer.emailAddress === trim(emailValue)) return true;
    const payload = {
      email: emailValue,
      channelId,
    };

    const { isValid }: { isValid: boolean } = isBCUser
      ? await checkUserBCEmail(payload)
      : await checkUserEmail(payload);

    return isValid;
  };

  const emailValidation = (data: Partial<ParamProps>) => {
    if (data.email !== customer.emailAddress && !data.currentPassword) {
      return false;
    }

    return true;
  };

  const passwordValidation = (data: Partial<ParamProps>) => {
    if (data.password !== data.confirmPassword) {
      return false;
    }

    return true;
  };

  return { isBCUser, companyId, customer, validateEmailValue, emailValidation, passwordValidation };
}

function AccountSetting() {
  const [sapPaymentData, setSapPaymentData] = useState<PaymentDataItem[] | null>(null);
  const [companyOpenAmounts, setCompanyOpenAmounts] = useState<any[]>([]);
  const { isBCUser, companyId, customer, validateEmailValue, emailValidation, passwordValidation } =
    useData();

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
    setError,
  } = useForm({
    mode: 'onSubmit',
  });

  const [isFinishUpdate, setIsFinishUpdate] = useStorageState<boolean>(
    'sf-isFinishUpdate',
    false,
    sessionStorage,
  );

  const {
    state: {
      portalStyle: { backgroundColor = '#FEF9F5' },
    },
  } = useContext(CustomStyleContext);

  const b3Lang = useB3Lang();

  const navigate = useNavigate();

  const [accountInfoFormFields, setAccountInfoFormFields] = useState<Partial<Fields>[]>([]);
  const [decryptionFields, setDecryptionFields] = useState<Partial<Fields>[]>([]);
  const [extraFields, setExtraFields] = useState<Partial<Fields>[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [accountSettings, setAccountSettings] = useState<any>({});
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [accountData, setAccountData] = useState<any>({});

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const fn = isBCUser ? getBCAccountSettings : getB2BAccountSettings;

        const params = isBCUser
          ? {}
          : {
              companyId,
            };

        const key = isBCUser ? 'customerAccountSettings' : 'accountSettings';

        const accountFormAllFields = await getB2BAccountFormFields(isBCUser ? 1 : 2);
        const accountFormFields = getAccountFormFields(
          accountFormAllFields.accountFormFields || [],
        );

        try {
          const resCompany = await getCompanyData(companyId);
          const accountId = resCompany?.data?.extraFields.find(
            (field: any) => field.fieldName === 'account_id',
          )?.fieldValue;
          if (!accountId) {
            throw new Error('Account ID not found');
          }

          const customerAccount = await fetchCustomerAccount(accountId);
          const companyOpenAmountsData = await fetchCompanyOpenAmounts(accountId);

          setAccountData(customerAccount);

          const openAmounts = Array.isArray(companyOpenAmountsData.AccountOpenAmounts)
            ? companyOpenAmountsData.AccountOpenAmounts
            : [companyOpenAmountsData.AccountOpenAmounts];

          const paymentDataRaw = customerAccount?.Customer?.PaymentData;

          const paymentDataArray = Array.isArray(paymentDataRaw)
            ? paymentDataRaw
            : paymentDataRaw
            ? [paymentDataRaw]
            : [];

          const filtered = paymentDataArray.filter((item: any) => item.CompanyID === '1000');

          setSapPaymentData(Array.isArray(filtered) ? filtered : [filtered]);
          setCompanyOpenAmounts(openAmounts);
        } catch (error) {
          console.log('error', error);
        }

        const contactInformation = (accountFormFields?.contactInformation || []).filter(
          (item: Partial<Fields>) => item.fieldId !== 'field_email_marketing_newsletter',
        );

        const { additionalInformation = [] } = accountFormFields;

        const { [key]: accountSettings } = await fn(params);

        const fields = isBCUser
          ? initBcInfo(accountSettings, contactInformation, additionalInformation)
          : initB2BInfo(
              accountSettings,
              contactInformation,
              getAccountSettingsFields(),
              additionalInformation,
            );

        const passwordModifiedFields = getPasswordModifiedFields();

        const all = [...fields, ...passwordModifiedFields];

        const roleItem = all.find((item) => item.name === 'role');

        if (roleItem?.fieldType) roleItem.fieldType = 'text';

        setAccountInfoFormFields(all);

        setAccountSettings(accountSettings);

        setDecryptionFields(contactInformation);

        setExtraFields(additionalInformation);
      } finally {
        if (isFinishUpdate) {
          snackbar.success(b3Lang('accountSettings.notification.detailsUpdated'));
          setIsFinishUpdate(false);
        }
        setLoading(false);
        setIsVisible(true);
      }
    };

    init();
    // disabling as we only need to run this once and values at starting render are good enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetUserExtraFields = (
    data: CustomFieldItems,
    accountInfoFormFields: Partial<Fields>[],
  ) => {
    const userExtraFields = accountInfoFormFields.filter(
      (item: CustomFieldItems) => item.custom && item.groupId === 1,
    );
    return userExtraFields.map((item: CustomFieldItems) => ({
      fieldName: deCodeField(item?.name || ''),
      fieldValue: data[item.name],
    }));
  };

  const handleAddUserClick = () => {
    handleSubmit(async (data: CustomFieldItems) => {
      setLoading(true);

      try {
        const isValid = await validateEmailValue(data.email);

        if (!isValid) {
          setError('email', {
            type: 'custom',
            message: b3Lang('accountSettings.notification.emailExists'),
          });
        }

        const emailFlag = emailValidation(data);

        if (!emailFlag) {
          snackbar.error(b3Lang('accountSettings.notification.updateEmailPassword'));
        }

        const passwordFlag = passwordValidation(data);

        if (!passwordFlag) {
          setError('confirmPassword', {
            type: 'manual',
            message: b3Lang('global.registerComplete.passwordMatchPrompt'),
          });
          setError('password', {
            type: 'manual',
            message: b3Lang('global.registerComplete.passwordMatchPrompt'),
          });
        }

        if (isValid && emailFlag && passwordFlag) {
          const dataProcessingFn = isBCUser ? bcSubmitDataProcessing : b2bSubmitDataProcessing;
          const payload = dataProcessingFn(data, accountSettings, decryptionFields, extraFields);

          if (payload) {
            if (!isBCUser) {
              payload.companyId = companyId;
              payload.extraFields = handleGetUserExtraFields(data, accountInfoFormFields);
            }

            if (payload.newPassword === '' && payload.confirmPassword === '') {
              delete payload.newPassword;
              delete payload.confirmPassword;
            }
          }

          if (!payload) {
            snackbar.success(b3Lang('accountSettings.notification.noEdits'));
            return;
          }

          const requestFn = isBCUser ? updateBCAccountSettings : updateB2BAccountSettings;
          await requestFn(payload);

          if (
            (data.password && data.currentPassword) ||
            customer.emailAddress !== trim(data.email)
          ) {
            navigate('/login?loginFlag=loggedOutLogin');
          } else {
            B3SStorage.clear();
            setIsFinishUpdate(true);
            window.location.reload();
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  };

  const translatedFields = useMemo(() => {
    const fieldTranslations: Record<string, string> = {
      field_first_name: b3Lang('accountSettings.form.firstName'),
      field_last_name: b3Lang('accountSettings.form.lastName'),
      field_email: b3Lang('accountSettings.form.email'),
      field_phone_number: b3Lang('accountSettings.form.phoneNumber'),
      field_company: b3Lang('accountSettings.form.company'),
      field_role: b3Lang('accountSettings.form.role'),
      field_current_password: b3Lang('accountSettings.form.currentPassword'),
      field_password: b3Lang('accountSettings.form.password'),
      field_confirm_password: b3Lang('accountSettings.form.confirmPassword'),
    };

    return accountInfoFormFields.map((item) => ({
      ...item,
      label: fieldTranslations[item.fieldId ?? ''] ?? item.label,
    }));
  }, [accountInfoFormFields, b3Lang]);

  return (
    <B3Spin isSpinning={isLoading} background={backgroundColor}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          width: '100%',
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            flex: 2,
            minHeight: { xs: 'auto', md: '300px' },
            p: 2,
            bgcolor: '#fff',
            borderRadius: '4px',
            boxShadow: 1,
          }}
        >
          <B3CustomForm
            formFields={translatedFields}
            errors={errors}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
          <CustomButton
            sx={{
              mt: '28px',
              width: '100%',
              visibility: isVisible ? 'visible' : 'hidden',
            }}
            onClick={handleAddUserClick}
            variant="contained"
          >
            {b3Lang('accountSettings.button.saveUpdates')}
          </CustomButton>

          <Box
            sx={{
              mt: 4,
              p: 2,
              border: '1px solid #FFD699',
              borderRadius: '8px',
              backgroundColor: '#FFF9F0',

              width: '100%',
              alignSelf: { md: 'flex-start' },
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              Si deseas actualizar información relacionada con tu cuenta, condiciones de pago o
              detalles administrativos, no dudes en contactarnos directamente por correo
              electrónico.
            </Typography>
            <Typography variant="body2">
              📧 <strong>Email:</strong>{' '}
              <a
                href="mailto:gestion.comercial@igt.es"
                style={{ color: '#007BFF', textDecoration: 'none' }}
              >
                gestion.comercial@igt.es
              </a>
            </Typography>
          </Box>
        </Box>

        {sapPaymentData && (
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 'auto', md: '300px' },
              p: 2,
              bgcolor: '#f9f9f9',
              borderRadius: '4px',
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {b3Lang('accountSettings.paymentSectionTitle')}
            </Typography>
            {sapPaymentData.map((payment: PaymentDataItem, index: number) => {
              const companyData = companyOpenAmounts?.find(
                (c) => c.CompanyID === payment.CompanyID,
              );
              const salesArrangement = accountData?.Customer?.SalesArrangement || {};
              return (
                <Box
                  key={`payment-info-${
                    payment.UUID ? payment.UUID : `${payment.CompanyID}-${index}`
                  }`}
                  sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <Typography variant="subtitle1">
                    <strong>{b3Lang('accountSettings.payment.companyID')}</strong>{' '}
                    {payment.CompanyID}
                  </Typography>
                  {salesArrangement?.CashDiscountTermsCode && (
                    <Typography variant="body1">
                      <strong>{b3Lang('accountSettings.payment.method')}</strong>{' '}
                      <PaymentMethod code={salesArrangement.CashDiscountTermsCode} />
                    </Typography>
                  )}
                  <Typography variant="body1">
                    <strong>{b3Lang('accountSettings.payment.creditLimit')}</strong>{' '}
                    {payment.CreditLimitAmount
                      ? formatCurrency(
                          payment.CreditLimitAmount['#'],
                          payment.CreditLimitAmount['@currencyCode'],
                        )
                      : formatCurrency('0', 'EUR')}
                  </Typography>
                  <Typography variant="body1">
                    <strong>{b3Lang('accountSettings.payment.currentSpendingLimit')}</strong>{' '}
                    {(() => {
                      const receivables = parseFloat(
                        companyData?.ReceivablesBalanceAmount?.['#'] || '0',
                      );
                      const salesOrder = parseFloat(companyData?.SalesOrderAmount?.['#'] || '0');
                      const serviceOrder = parseFloat(
                        companyData?.ServiceOrderAmount?.['#'] || '0',
                      );
                      const total = receivables + salesOrder + serviceOrder;
                      const currencyCode =
                        companyData?.ReceivablesBalanceAmount?.['@currencyCode'] || 'EUR';

                      return formatCurrency(total.toString(), currencyCode);
                    })()}
                  </Typography>
                  <Typography variant="body1">
                    <strong>{b3Lang('accountSettings.payment.receivablesBalance')}</strong>{' '}
                    {companyData && companyData.CurrentSpendingLimitAmount
                      ? formatCurrency(
                          companyData.CurrentSpendingLimitAmount['#'],
                          companyData.CurrentSpendingLimitAmount['@currencyCode'],
                        )
                      : formatCurrency('0', 'EUR')}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </B3Spin>
  );
}

export default AccountSetting;
